/**
 * Environment Configuration Service
 * Handles environment variables for different deployment contexts:
 * - Development: import.meta.env.VITE_*
 * - Staging/Production: window.__RUNTIME_CONFIG__.*
 */

export interface RuntimeConfig {
  // API Configuration
  VITE_API_BASE_URL: string
  VITE_GRAPHQL_ENDPOINT: string
  VITE_GRAPHQL_URI: string
  VITE_GRAPHQL_CREDENTIALS: string

  // API Client Configuration
  VITE_API_RETRIES: string
  VITE_API_RETRY_DELAY: string
  VITE_API_TIMEOUT: string

  // Application Configuration
  VITE_APP_NAME: string
  VITE_APP_VERSION: string
  VITE_NODE_ENV: string

  // Feature Flags
  VITE_MAINTENANCE_MODE: string
  VITE_MAINTENANCE_TITLE?: string
  VITE_MAINTENANCE_MESSAGE?: string
  VITE_MAINTENANCE_ESTIMATED_TIME?: string
  VITE_MAINTENANCE_CONTACT_EMAIL?: string
  VITE_MAINTENANCE_SHOW_RETRY?: string

  // Debug Configuration
  VITE_DEBUG_MODE: string

  // Authentication Configuration
  VITE_AUTH_TOKEN_KEY?: string
  VITE_AUTH_REFRESH_TOKEN_KEY?: string
  VITE_AUTH_SESSION_KEY?: string

  // UI Configuration
  VITE_THEME_KEY?: string
  VITE_LANGUAGE_KEY?: string

  // Mediator Configuration
  VITE_MEDIATOR_NUMBER?: string
  VITE_MEDIATOR_MESSAGE?: string

  // Pre-registration Configuration
  VITE_PRE_REGISTRATION_URL?: string
  VITE_PRE_REGISTRATION_SHARE_TITLE?: string
  VITE_PRE_REGISTRATION_SHARE_TEXT?: string
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig
  }
}

export interface AppConfig {
  api: {
    baseUrl: string
    timeout: number
  }
  graphql: {
    uri: string
    credentials: 'omit' | 'same-origin' | 'include'
    timeout: number
  }
  auth: {
    tokenKey: string
    refreshTokenKey: string
    sessionKey: string
  }
  app: {
    name: string
    version: string
    environment: 'development' | 'staging' | 'production'
  }
  features: {
    maintenanceMode: boolean
    debugMode: boolean
  }
  maintenance: {
    title: string
    message: string
    estimatedTime?: string
    contactEmail?: string
    showRetry: boolean
  }
  ui: {
    themeKey: string
    languageKey: string
  }
  mediator: {
    number: string
    message: string
  }
  preRegistration: {
    url: string
    shareTitle: string
    shareText: string
  }
}

class ConfigService {
  private static instance: ConfigService
  private config: AppConfig
  private isRuntimeConfigLoaded: boolean = false

  private constructor() {
    this.config = this.loadConfig()
  }

  static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService()
    }
    return ConfigService.instance
  }

  private isDevelopment(): boolean {
    return import.meta.env.DEV || import.meta.env.MODE === 'development'
  }

  private getEnvValue(key: string, fallback: string = ''): string {
    // Ensure key always has VITE_ prefix
    const viteKey = key.startsWith('VITE_') ? key : `VITE_${key}`

    if (this.isDevelopment()) {
      // Development: use import.meta.env.VITE_*
      return import.meta.env[viteKey] || fallback
    } else {
      // Production/Staging: use window.__RUNTIME_CONFIG__.VITE_*
      return (
        window.__RUNTIME_CONFIG__?.[viteKey as keyof RuntimeConfig] || fallback
      )
    }
  }

  private loadConfig(): AppConfig {
    return {
      api: {
        baseUrl: this.getEnvValue('VITE_API_BASE_URL', 'http://localhost:3000'),
        timeout: parseInt(this.getEnvValue('VITE_API_TIMEOUT', '30000'), 10),
      },
      graphql: {
        uri: this.getEnvValue(
          'VITE_GRAPHQL_URI',
          'http://localhost:3000/graphql'
        ),
        credentials: this.getEnvValue('VITE_GRAPHQL_CREDENTIALS', 'include') as
          | 'omit'
          | 'same-origin'
          | 'include',
        timeout: parseInt(this.getEnvValue('VITE_API_TIMEOUT', '30000'), 10),
      },
      auth: {
        tokenKey: this.getEnvValue(
          'VITE_AUTH_TOKEN_KEY',
          'garupa_socio_colaborador_access_token'
        ),
        refreshTokenKey: this.getEnvValue(
          'VITE_AUTH_REFRESH_TOKEN_KEY',
          'garupa_socio_colaborador_refresh_token'
        ),
        sessionKey: this.getEnvValue(
          'VITE_AUTH_SESSION_KEY',
          'garupa_socio_colaborador_auth_session'
        ),
      },
      app: {
        name: this.getEnvValue('VITE_APP_NAME', 'Garupa Sócio Colaborador'),
        version: this.getEnvValue('VITE_APP_VERSION', '1.0.0'),
        environment: this.getEnvValue('VITE_NODE_ENV', 'development') as
          | 'development'
          | 'staging'
          | 'production',
      },
      features: {
        maintenanceMode:
          this.getEnvValue('VITE_MAINTENANCE_MODE', 'false') === 'true',
        debugMode: this.getEnvValue('VITE_DEBUG_MODE', 'false') === 'true',
      },
      maintenance: {
        title: this.getEnvValue(
          'VITE_MAINTENANCE_TITLE',
          'Sistema em Manutenção'
        ),
        message: this.getEnvValue(
          'VITE_MAINTENANCE_MESSAGE',
          'Estamos realizando melhorias no sistema para oferecer uma melhor experiência. Voltaremos em breve.'
        ),
        estimatedTime: this.getEnvValue('VITE_MAINTENANCE_ESTIMATED_TIME'),
        contactEmail: this.getEnvValue('VITE_MAINTENANCE_CONTACT_EMAIL'),
        showRetry:
          this.getEnvValue('VITE_MAINTENANCE_SHOW_RETRY', 'true') === 'true',
      },
      ui: {
        themeKey: this.getEnvValue(
          'VITE_THEME_KEY',
          'garupa_socio_colaborador_theme_preference'
        ),
        languageKey: this.getEnvValue(
          'VITE_LANGUAGE_KEY',
          'garupa_socio_colaborador_language_preference'
        ),
      },
      mediator: {
        number: this.getEnvValue('VITE_MEDIATOR_NUMBER', ''),
        message: this.getEnvValue(
          'VITE_MEDIATOR_MESSAGE',
          'Olá, preciso de ajuda.'
        ),
      },
      preRegistration: {
        url: this.getEnvValue('VITE_PRE_REGISTRATION_URL', ''),
        shareTitle: this.getEnvValue(
          'VITE_PRE_REGISTRATION_SHARE_TITLE',
          'Cadastro de Motorista - Garupa'
        ),
        shareText: this.getEnvValue(
          'VITE_PRE_REGISTRATION_SHARE_TEXT',
          'Complete seu cadastro para se tornar um motorista Garupa'
        ),
      },
    }
  }

  /**
   * Wait for runtime configuration to be loaded in production
   */
  async waitForRuntimeConfig(timeout: number = 5000): Promise<void> {
    if (this.isDevelopment() || this.isRuntimeConfigLoaded) {
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      const startTime = Date.now()

      const checkConfig = () => {
        if (window.__RUNTIME_CONFIG__) {
          this.isRuntimeConfigLoaded = true
          this.config = this.loadConfig() // Reload config with runtime values
          resolve()
        } else if (Date.now() - startTime > timeout) {
          console.warn(
            'Runtime config not loaded within timeout, using defaults'
          )
          reject(new Error('Runtime configuration timeout'))
        } else {
          setTimeout(checkConfig, 100)
        }
      }

      checkConfig()
    })
  }

  /**
   * Reload configuration (useful after runtime config is loaded)
   */
  reloadConfig(): void {
    this.config = this.loadConfig()
  }

  getConfig(): AppConfig {
    return this.config
  }

  get api() {
    return this.config.api
  }

  get graphql() {
    return this.config.graphql
  }

  get auth() {
    return this.config.auth
  }

  get app() {
    return this.config.app
  }

  get features() {
    return this.config.features
  }

  get maintenance() {
    return this.config.maintenance
  }

  get ui() {
    return this.config.ui
  }

  get mediator() {
    return this.config.mediator
  }

  get preRegistration() {
    return this.config.preRegistration
  }

  isDev(): boolean {
    return this.config.app.environment === 'development'
  }

  isStaging(): boolean {
    return this.config.app.environment === 'staging'
  }

  isProduction(): boolean {
    return this.config.app.environment === 'production'
  }

  isMaintenanceMode(): boolean {
    return this.config.features.maintenanceMode
  }

  isDebugMode(): boolean {
    return this.config.features.debugMode
  }
}

export const configService = ConfigService.getInstance()
